import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { Button } from './ui/button';
import { Radio, User, LogOut, Globe, ShieldCheck, Store, ShoppingBag, ShoppingCart } from 'lucide-react';
import { useCart } from '../lib/cart';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from './ui/dropdown-menu';

export default function Header() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { count: cartCount } = useCart();
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-40 border-b border-black/10 bg-[#E5E7EB] text-black shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-link" className="shrink-0"><Logo /></Link>
        <div className="flex items-center gap-1 sm:gap-2 min-w-0 shrink">
          <span className="hidden sm:inline-flex"><ThemeToggle /></span>
          <Button variant="ghost" size="sm" data-testid="lang-toggle-button"
            onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
            className="hidden sm:inline-flex text-black hover:bg-black/5 hover:text-black">
            <Globe className="h-4 w-4 mr-1" />{lang.toUpperCase()}
          </Button>
          <Link to="/shop" data-testid="header-shop-link" className="hidden sm:inline-block text-sm font-medium text-black hover:text-black/60 px-3 py-1.5">Boutique</Link>
          <Link to="/cart" data-testid="header-cart-link" className="relative inline-flex items-center text-black hover:text-black/60 px-2 py-1.5" aria-label="Panier">
            <ShoppingCart className="h-5 w-5" />
            {cartCount > 0 && <span data-testid="header-cart-count" className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-black text-[#FFD400] text-[10px] font-bold grid place-items-center">{cartCount}</span>}
          </Link>
          <Link to="/contact" data-testid="header-contact-link" className="hidden sm:inline-block text-sm font-medium text-black hover:text-black/60 px-3 py-1.5">Contact</Link>
          {user?.role === 'admin' && (
            <Link to="/admin" data-testid="header-admin-link" className="inline-flex items-center gap-1 text-sm font-semibold text-black bg-[#FFD400] hover:bg-[#ffe03a] rounded-full px-3 py-1.5">
              <ShieldCheck className="h-4 w-4" /><span className="hidden sm:inline">Admin</span>
            </Link>
          )}
          {user?.role === 'seller' && (
            <Link to="/seller" data-testid="header-seller-link" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-black btn-yellow rounded-full px-3 py-1.5">
              <Store className="h-4 w-4" />Espace vendeur
            </Link>
          )}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="user-menu-trigger" className="text-black hover:bg-black/5 hover:text-black">
                  <User className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">{user.name.split(' ')[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-slate-900 border-slate-800 text-slate-100">
                <DropdownMenuLabel className="text-slate-400">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-slate-800" />
                <DropdownMenuItem data-testid="menu-buyer" onClick={() => nav('/buyer')}>
                  <ShoppingBag className="h-4 w-4 mr-2" />{t('buyer_dashboard')}
                </DropdownMenuItem>
                {(user.role === 'seller' || user.role === 'admin') && (
                  <DropdownMenuItem data-testid="menu-seller" onClick={() => nav('/seller')}>
                    <Store className="h-4 w-4 mr-2" />{t('seller_dashboard')}
                  </DropdownMenuItem>
                )}
                {user.role === 'admin' && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => nav('/admin')}>
                    <ShieldCheck className="h-4 w-4 mr-2" />{t('admin_dashboard')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-slate-800" />
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav('/'); }}>
                  <LogOut className="h-4 w-4 mr-2" />{t('logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" size="sm" data-testid="header-login-btn"
                onClick={() => nav('/login')} className="text-black hover:bg-black/5 hover:text-black">{t('login')}</Button>
              <Button size="sm" data-testid="header-register-btn"
                onClick={() => nav('/register')}
                className="btn-yellow text-black font-bold">{t('register')}</Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
